import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography, alpha, useTheme,
} from '@mui/material';
import { FileDownloadOutlined, PictureAsPdfOutlined, TableChartOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

/** "Download" with a PDF report or a CSV of what the page currently shows. */
const DownloadMenu: React.FC<{
  onExport: (format: 'pdf' | 'csv') => Promise<void>;
  /** The menu's caption — what the export covers. */
  heading?: string;
  /** Nothing to export yet. */
  disabled?: boolean;
}> = ({ onExport, heading = 'Export with current filters', disabled }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'csv' | null>(null);

  const run = async (format: 'pdf' | 'csv') => {
    setAnchor(null);
    setExporting(format);
    try {
      await onExport(format);
    } catch (err: any) {
      enqueueSnackbar(err?.message || `Could not export the ${format.toUpperCase()}`, { variant: 'error' });
    } finally {
      setExporting(null);
    }
  };

  const option = (format: 'pdf' | 'csv', icon: React.ReactNode, primary: string, secondary: string) => (
    <MenuItem onClick={() => run(format)} sx={{ py: 1.5, px: 2 }}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={primary} secondary={secondary}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
    </MenuItem>
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined fontSize="small" />}
        onClick={e => setAnchor(e.currentTarget)}
        disabled={disabled || !!exporting}
        sx={{
          textTransform: 'none', fontWeight: 500, borderColor: theme.palette.divider, color: theme.palette.text.secondary,
          '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04), color: theme.palette.primary.main },
        }}
      >
        {exporting ? `Exporting ${exporting.toUpperCase()}…` : 'Download'}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 240, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {heading}
          </Typography>
        </Box>
        <Divider />
        {option('pdf', <PictureAsPdfOutlined fontSize="small" sx={{ color: '#D32F2F' }} />, 'Download PDF', 'Formatted report with summary')}
        {option('csv', <TableChartOutlined fontSize="small" sx={{ color: '#2E7D32' }} />, 'Download CSV', 'Spreadsheet-compatible data')}
      </Menu>
    </>
  );
};

export default DownloadMenu;
